// Intent: serialize / restore ALL on-device app data as one JSON blob, for the
// opt-in cloud backup. The blob is opaque to the server (it only stores/returns
// it); the shape lives here. Restore is additive (upsert by id) — it never
// deletes local rows that aren't in the snapshot, so a stale restore can't wipe
// newer local-only entries.

import { getDb, KV_HOUSEHOLD } from '@/db';
import { TABLES, type TableName } from '@/db/types';

interface DocWithId {
  id: string;
}

export interface AppSnapshot {
  v: 1;
  tables: Record<string, DocWithId[]>;
  household: string | null;
}

/** Read every table + the household kv into a single snapshot object. */
export async function exportSnapshot(): Promise<AppSnapshot> {
  const db = await getDb();
  const tables: Record<string, DocWithId[]> = {};
  for (const table of Object.values(TABLES)) {
    tables[table] = await db.list<DocWithId>(table);
  }
  const household = await db.getKv(KV_HOUSEHOLD);
  return { v: 1, tables, household };
}

/** Write a snapshot back into local storage (upsert by id + household kv). */
export async function importSnapshot(snapshot: AppSnapshot): Promise<void> {
  if (!snapshot || snapshot.v !== 1) return;
  const db = await getDb();
  for (const table of Object.values(TABLES)) {
    const rows = snapshot.tables?.[table] ?? [];
    for (const row of rows) {
      if (row && typeof row.id === 'string') {
        await db.upsert(table as TableName, row.id, row);
      }
    }
  }
  if (typeof snapshot.household === 'string') {
    await db.setKv(KV_HOUSEHOLD, snapshot.household);
  }
}

// --- Selective snapshot for the SHARED household sync ---------------------
// Household sync shares only the collaborative data (budget/goals/income/fixed
// costs + household kv) — never policies (private per account) and never the
// vault (which isn't even a TABLE). These helpers leave the account-wide
// exportSnapshot/importSnapshot above untouched so the personal cloud backup
// keeps working exactly as before.

/** Tables shared inside a household. Policies stay private; reminders are local. */
export const HOUSEHOLD_TABLES: TableName[] = [
  TABLES.entries,
  TABLES.goals,
  TABLES.contributions,
  TABLES.incomeSources,
  TABLES.incomeEntries,
  TABLES.fixedCosts,
];

interface RowWithTimestamps extends DocWithId {
  updatedAt?: string;
  createdAt?: string;
}

/** Best-effort modification time for last-write-wins (updatedAt, else createdAt). */
function stampOf(row: RowWithTimestamps): string {
  return row.updatedAt ?? row.createdAt ?? '';
}

/** Export only the given tables + household kv (subset snapshot). */
export async function exportSnapshotOf(tables: TableName[], includeHousehold = true): Promise<AppSnapshot> {
  const db = await getDb();
  const out: Record<string, DocWithId[]> = {};
  for (const table of tables) {
    out[table] = await db.list<DocWithId>(table);
  }
  const household = includeHousehold ? await db.getKv(KV_HOUSEHOLD) : null;
  return { v: 1, tables: out, household };
}

/**
 * Merge a household snapshot into local storage with ROW-LEVEL last-write-wins:
 * an incoming row wins only when the local row is missing OR the incoming row is
 * newer (by updatedAt/createdAt). Append-only rows (contributions, income
 * entries) therefore never clobber a locally-edited newer copy.
 */
export async function importSnapshotMerge(snapshot: AppSnapshot, tables: TableName[]): Promise<void> {
  if (!snapshot || snapshot.v !== 1) return;
  const db = await getDb();
  for (const table of tables) {
    const incoming = (snapshot.tables?.[table] ?? []) as RowWithTimestamps[];
    if (!incoming.length) continue;
    const local = await db.list<RowWithTimestamps>(table);
    const localById = new Map(local.map((r) => [r.id, r]));
    for (const row of incoming) {
      if (!row || typeof row.id !== 'string') continue;
      const mine = localById.get(row.id);
      if (!mine || stampOf(row) > stampOf(mine)) {
        await db.upsert(table, row.id, row);
      }
    }
  }
  // Household kv: only the name/declarations travel; last-write-wins is fine
  // because the server blob already reflects the freshest push.
  if (typeof snapshot.household === 'string') {
    await db.setKv(KV_HOUSEHOLD, snapshot.household);
  }
}
